// ** React Imports
import React from 'react'

// ** Child Imports
import {
  Icon,
  IconButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@shopflo/ui'

// ** Types Imports
import { SegmentListItemType } from 'src/lib/types/segment'

// ** Utils
import { classNames, getIstTimeString } from 'src/lib/utils/helpers'
import { imagePaths } from 'src/lib/utils/constants'
import { DotsThreeVertical, Spinner, AddressBook, PingPong, Info } from '@phosphor-icons/react'

// ** Child Imports
import { ColumnDef } from '@tanstack/react-table'
import { useLocale } from 'src/lib/hooks/useLocal'
import { ScrollMode } from '../common/table/table-types'
import GenericTableV2 from '../common/table/GenericTableV2'
import ImageContainer from '../common/ImageContainer'

/** Matches phosphor Spinner at h-4 w-4 — keeps dots aligned with in-progress row */
const STATUS_LEADING_ICON_BOX_CLASS = 'flex h-4 w-4 shrink-0 items-center justify-center'

interface SegmentTableProps {
  segments: SegmentListItemType[]
  loading: boolean
  hasMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
  onRowClick: (segment: SegmentListItemType) => void
  handleEditSegment: (segment: SegmentListItemType) => void
  handleToggleSegment: (segmentId: string, isEnabled: boolean) => void
  handleDeleteSegment: (segmentId: string) => void
}

const SegmentTable: React.FC<SegmentTableProps> = ({
  segments,
  loading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onRowClick,
  handleEditSegment,
  handleToggleSegment,
  handleDeleteSegment
}) => {
  const { t } = useLocale()

  const tableColumns: ColumnDef<SegmentListItemType>[] = [
    {
      id: 'name',
      header: t('segment_creation.table_header_name'),
      minSize: 300,
      cell: ({ row }) => {
        const originalRow = row.original
        const isShopify = originalRow.type === 'CUSTOMER_TAG'
        const IconComponent = originalRow.constructType === 'STATIC' ? AddressBook : PingPong
        const iconColor = originalRow.constructType === 'STATIC' ? 'text-pink-500' : 'text-teal-500'

        return (
          <div className='flex items-center gap-2 py-2.5 px-2'>
            {isShopify ? (
              <span className='flex h-6 w-6 shrink-0 items-center justify-center'>
                <ImageContainer
                  height={22}
                  width={22}
                  imageSource={imagePaths.SHOPIFY_LOGO}
                  defaultImage={imagePaths.IMAGE_PLACEHOLDER}
                  altText={t('segment_creation.shopify_tag_title')}
                />
              </span>
            ) : (
              <Icon IconComponent={IconComponent} className={iconColor} size='md' iconProps={{weight:'duotone'}}/>
            )}
            {/* 4px gap to the trailing info affordance; min-w-0 keeps the name truncating */}
            <div className='flex min-w-0 items-center gap-1'>
              <p className='text-text-sm-medium text-zinc-800 truncate'>{originalRow.name}</p>
              {Boolean(originalRow.description) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='flex shrink-0 cursor-pointer items-center'>
                      <Icon
                        IconComponent={Info}
                        size='xxs'
                        className='text-zinc-500'
                        iconProps={{ weight: 'regular' }}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side='top' className='max-w-[280px] text-wrap text-left'>
                    {originalRow.description}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        )
      }
    },
    {
      id: 'type',
      header: t('segment_creation.table_header_type'),
      size: 80,
      cell: ({ row }) => {
        const originalRow = row.original
        const isStatic = originalRow.type === 'PHONE_NUMBER' || originalRow.type === 'PINCODE'
        const isShopify = originalRow.type === 'CUSTOMER_TAG'
        const typeLabel = isShopify
          ? t('segment_creation.shopify_tag_title')
          : isStatic
            ? t('segment_creation.type_static')
            : t('segment_creation.type_dynamic')
        return (
          <div className='flex flex-col px-2'>
            <p className='text-text-xs-regular text-zinc-600'>{typeLabel}</p>
          </div>
        )
      }
    },
    {
      id: 'status',
      header: t('segment_creation.table_header_status'),
      size: 80,
      cell: ({ row }) => {
        const originalRow = row.original
        const getStatusLabel = () => {
          if (originalRow.status === 'ACTIVE' || originalRow.constructType === 'STATIC') {
            const active = originalRow.isEnabled
            return (
              <div className='flex items-center gap-1'>
                <div className={STATUS_LEADING_ICON_BOX_CLASS}>
                  <div className={classNames('h-1.5 w-1.5 rounded-full', active ? 'bg-lime-600' : 'bg-zinc-400')} />
                </div>
                <span className={classNames('text-text-xs-medium', active ? 'text-lime-600' : 'text-zinc-500')}>
                  {active ? t('segment_creation.status_active') : t('segment_creation.status_inactive')}
                </span>
              </div>
            )
          }
          if (originalRow.status === 'FAILED') {
            return (
              <div className='flex items-center gap-1'>
                <div className={STATUS_LEADING_ICON_BOX_CLASS}>
                  <div className='h-2 w-2 rounded-full bg-red-600' />
                </div>
                <span className='text-text-xs-medium text-red-600'>{t('segment_creation.status_failed')}</span>
              </div>
            )
          }
          return (
            <div className='flex items-center gap-1'>
              <div className={STATUS_LEADING_ICON_BOX_CLASS}>
                <Spinner weight='regular' className='h-4 w-4 text-amber-600 animate-spin' />
              </div>
              <span className='text-text-xs-medium text-amber-700'>{t('segment_creation.status_in_progress')}</span>
            </div>
          )
        }
        return <div className='px-2'>{getStatusLabel()}</div>
      }
    },
    {
      id: 'createdAt',
      header: t('segment_creation.table_header_created_date'),
      size: 80,
      cell: ({ row }) => {
        const originalRow = row.original
        return (
          <div className='flex flex-col px-2'>
            <p className='text-text-xs-regular text-zinc-600'>{getIstTimeString(originalRow.createdAt, 'DATE')}</p>
          </div>
        )
      }
    },
    {
      id: 'actions',
      header: ' ',
      size: 48,
      cell: ({ row }) => {
        const originalRow = row.original
        const handleAction = (value: string) => {
          if (value === 'edit') handleEditSegment(originalRow)
          if (value === 'toggle') handleToggleSegment(originalRow.id, !originalRow.isEnabled)
          if (value === 'delete') handleDeleteSegment(originalRow.id)
        }
        return (
          <div className='flex w-full items-center justify-end px-2' onClick={e => e.stopPropagation()}>
            <Select value='' onValueChange={handleAction}>
              <SelectTrigger
                className='flex h-8 w-8 items-center justify-center border-none bg-transparent p-0 shadow-none outline-none focus:ring-0'
                showCaret={false}
              >
                <IconButton
                  IconComponent={DotsThreeVertical}
                  size='sm'
                  variant='ghost'
                  className='text-zinc-500'
                  iconProps={{ weight: 'bold' }}
                />
              </SelectTrigger>
              <SelectContent align='end' className='min-w-[140px]'>
                <SelectItem value='edit' className='cursor-pointer'>
                  {t('edit')}
                </SelectItem>
                <SelectItem value='toggle' className='cursor-pointer'>
                  {originalRow.isEnabled ? t('disable') : t('enable')}
                </SelectItem>
                <SelectItem value='delete' className='cursor-pointer'>
                  {t('delete')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )
      }
    }
  ]

  return (
    <div className='flex w-full min-w-0 flex-col'>
      <GenericTableV2
        data={segments}
        columns={tableColumns}
        infiniteScroll={{
          hasMore,
          onLoadMore,
          isLoading: isLoadingMore
        }}
        isLoading={loading}
        onRowClick={(row: SegmentListItemType) => onRowClick(row)}
        scrollMode={ScrollMode.INFINITE}
        stickyHeaderTop={0}
        scrollInParent
        roundedRows
      />
    </div>
  )
}

export default SegmentTable
